import { useState } from 'react'
import Layout, { Card } from '@/components/Layout'
import { router } from '@inertiajs/react'
import type { AccessLevel } from '@/types'

interface Share { user_id: number; email: string; access: 'read' | 'write' }
interface PersonRow {
  id: number; name: string; balance: number; total: number
  streak: number; goals_count: number; is_current: boolean
  access: AccessLevel; is_owner: boolean; owner_email: string
  shares?: Share[]
}

const ACCESS_BADGE: Record<AccessLevel, { label: string; cls: string }> = {
  owner: { label: '拥有者', cls: 'bg-[#eef2ff] text-[#5b7cfa]' },
  write: { label: '可写', cls: 'bg-[#e9f9ef] text-[#1d7a44]' },
  read: { label: '只读', cls: 'bg-[#fff4e5] text-[#b86e00]' },
}

function ShareManager({ person }: { person: PersonRow }) {
  const [email, setEmail] = useState('')
  const [access, setAccess] = useState<'read' | 'write'>('read')

  const share = () => {
    if (!email.trim()) return
    router.post(`/people/${person.id}/share`, { email: email.trim(), access }, { onSuccess: () => setEmail('') })
  }
  const unshare = (userId: number) =>
    router.delete(`/people/${person.id}/unshare`, { data: { user_id: userId } })

  return (
    <div className="mt-2 pl-3 border-l-2 border-[#eef2ff]">
      {person.shares && person.shares.length > 0 ? (
        <div className="grid gap-1.5">
          {person.shares.map((s) => (
            <div key={s.user_id} className="flex items-center gap-2 text-[13px]">
              <span className="text-[#707d90]">🔗 {s.email}</span>
              <span className={'px-2 py-px rounded-md ' + ACCESS_BADGE[s.access].cls}>{ACCESS_BADGE[s.access].label}</span>
              <button onClick={() => unshare(s.user_id)} className="text-[#707d90] hover:text-[#ef5350]">取消</button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[#707d90] text-[13px]">尚未分享给其他人</div>
      )}
      <div className="flex gap-2 flex-wrap items-center mt-2">
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="对方邮箱"
          className="flex-1 min-w-[150px] border border-[#e7ebf2] rounded-[10px] px-2.5 py-1.5 text-[13px] outline-none focus:border-[#5b7cfa]"
        />
        <select
          value={access}
          onChange={(e) => setAccess(e.target.value as 'read' | 'write')}
          className="border border-[#e7ebf2] rounded-[10px] px-2 py-1.5 text-[13px] outline-none focus:border-[#5b7cfa]"
        >
          <option value="read">只读</option>
          <option value="write">可写</option>
        </select>
        <button onClick={share} className="bg-[#5b7cfa] text-white font-semibold text-[13px] px-3 py-1.5 rounded-[10px]">分享</button>
      </div>
    </div>
  )
}

export default function Index({ people }: { people: PersonRow[] }) {
  const [name, setName] = useState('')

  const add = () => { if (!name.trim()) return; router.post('/people', { name }, { onSuccess: () => setName('') }) }
  const rename = (p: PersonRow) => {
    const v = window.prompt('重命名人员：', p.name)
    if (v && v.trim()) router.patch(`/people/${p.id}`, { name: v.trim() })
  }
  const remove = (p: PersonRow) => {
    if (window.confirm(`删除「${p.name}」及其全部目标、积分与记录？不可恢复。`)) router.delete(`/people/${p.id}`)
  }
  const leave = (p: PersonRow) => {
    if (window.confirm(`退出对「${p.name}」的共享？之后将无法再查看/管理它。`)) router.delete(`/people/${p.id}/leave`)
  }

  return (
    <Layout>
      <Card title="👥 人员管理" extra={<span className="text-[#707d90] text-[13px]">共 {people.length} 人</span>}>
        {people.map((p) => {
          const canWrite = p.access === 'owner' || p.access === 'write'
          return (
            <div key={p.id} className="py-2.5 border-b border-dashed border-[#e7ebf2] last:border-0">
              <div className="flex items-center gap-2">
                <span className="flex-1">
                  <b className={p.is_current ? 'font-extrabold' : 'font-semibold'}>{p.name}</b>{' '}
                  <span className={'text-[11px] px-[7px] py-px rounded-md ' + ACCESS_BADGE[p.access].cls}>{ACCESS_BADGE[p.access].label}</span>
                  {p.is_current && <span className="text-[11px] px-[7px] py-px rounded-md bg-[#eef2ff] text-[#707d90] ml-1">当前</span>}
                  <div className="text-[#707d90] text-[13px]">
                    {p.balance} 分 · 🔥 {p.streak} 天 · 累计 {p.total} 分 · {p.goals_count} 个目标
                    {!p.is_owner && <> · 来自 {p.owner_email}</>}
                  </div>
                </span>
                {!p.is_current && <button onClick={() => router.post(`/people/${p.id}/switch`)} className="bg-[#eef2ff] text-[#5b7cfa] font-semibold text-[13px] px-3 py-1.5 rounded-[10px]">切换</button>}
                {canWrite && <button onClick={() => rename(p)} title="重命名" className="text-[#707d90] hover:text-[#5b7cfa] text-base px-1.5">✎</button>}
                {p.is_owner && <button onClick={() => remove(p)} title="删除" className="text-[#707d90] hover:text-[#ef5350] text-base px-1.5">🗑</button>}
                {!p.is_owner && <button onClick={() => leave(p)} title="退出共享" className="text-[#707d90] hover:text-[#ef5350] text-[13px] px-1.5">退出</button>}
              </div>
              {p.is_owner && <ShareManager person={p} />}
            </div>
          )
        })}
        <div className="flex gap-2.5 flex-wrap items-center mt-3.5">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="新人员名字，如 老大 / 老二 / 老三" className="flex-1 min-w-[130px] border border-[#e7ebf2] rounded-[10px] px-2.5 py-2 outline-none focus:border-[#5b7cfa]" />
          <button onClick={add} className="bg-[#5b7cfa] text-white font-semibold px-4 py-2 rounded-[10px]">添加人员</button>
        </div>
        <div className="text-[#707d90] text-[13px] mt-2.5">提示：你创建的人员你是「拥有者」，可分享给其他账号（只读 / 可写）一起管理。删除人员会一并清除其全部数据，仅拥有者可删除。</div>
      </Card>
    </Layout>
  )
}
